import Dexie from 'dexie'
import { ForemanDB } from '../../types/IndexedDB/ForemanDB'
import { Employee } from '../../types/Employee/Employee'
import { ReworkTask } from '../../types/Work/WorkItemTask'
import { WorkItemTask } from '../../types/Work/WorkItemTask'
import { WorkItemHierarchy, WorkItemInfo } from '../../types/Work/WorkItemInfo'
import { buildHierarchy } from '../../utils/WorkItemsTreeUtils'
import { ShiftEndTaskSummary } from '../../types/Reports/ShiftEnd/ShiftEndWorkItems'
import { getTransactionTables } from '../../utils/MutationSnapshotUtils'

//#region READ
export const getForemanWorkItemsFB = async (db: ForemanDB): Promise<Employee> => {
	const employeeCache = await db.employee.toArray()
	const workItemCache = await db.workItems.toArray()
	const taskCache = await db.tasks.toArray()
	const reworkCache = await db.reworks.toArray()
	const constraintCache = await db.constraints.toArray()

	return {
		...employeeCache[0],
		workItems: buildHierarchy(workItemCache, taskCache, reworkCache, constraintCache),
	}
}
export const getDirectReportsWorkItemsFB = async (db: ForemanDB): Promise<Employee[]> => {
	const directReportsCache = await db.directReports.toArray()

	for (const directReport of directReportsCache) {
		const [workItemAssignments, taskAssignments, reworkAssignments] = await Promise.all([
			db.workItemAssignments.where('workerId').equals(directReport.id).toArray(),
			db.taskAssignments.where('workerId').equals(directReport.id).toArray(),
			db.reworkAssignments.where('workerId').equals(directReport.id).toArray(),
		])
		const [assignedTasks, assignedReworks, workItems] = await Promise.all([
			db.tasks
				.where('id')
				.anyOf(taskAssignments.map((ta) => ta.workItemTaskId))
				.toArray(),
			db.reworks
				.where('id')
				.anyOf(reworkAssignments.map((ra) => ra.reworkId))
				.toArray(),
			db.workItems
				.where('id')
				.anyOf(workItemAssignments.map((wia) => wia.workItemId))
				.toArray(),
		])
		if (directReport.activeReworkId) {
			assignedReworks.forEach((ra) => {
				if (ra.id === directReport.activeReworkId) {
					ra.isActive = true
				}
			})
		} else if (directReport.activeTaskId) {
			assignedTasks.forEach((ta) => {
				if (ta.id === directReport.activeTaskId) {
					ta.isActive = true
				}
			})
		}
		directReport.workItems = workItems
		directReport.workItems.forEach((wi) => {
			wi.tasks = assignedTasks.filter((ta) => ta.workItemId === wi.id)
			wi.reworks = assignedReworks.filter((ra) => ra.workItemId === wi.id)
		})
		const attendance = await db.attendance.where('workerId').equals(directReport.id).first()
		directReport.attendanceToday = attendance?.signOutAt
			? 'SignedOut'
			: attendance?.status ?? undefined
	}

	return directReportsCache
}
export const getWorkItemInfoFB = async (
	db: ForemanDB,
	workItemIds: number[],
): Promise<WorkItemInfo[]> => {
	const workItemCache = await db.workItems.toArray()
	const workItemInfo = workItemCache
		.filter((wi) => workItemIds.includes(wi.id))
		.map((wi) => {
			const hierarchy: WorkItemHierarchy[] = []
			let parentWi = workItemCache.find((w) => w.id === wi.parentId)
			hierarchy.push({
				name: wi.name,
				id: wi.id,
				alias: wi.wbsName,
				index: wi.wbsIndex,
			})
			while (parentWi) {
				hierarchy.push({
					name: parentWi.name,
					id: parentWi.id,
					alias: parentWi.wbsName,
					index: parentWi.wbsIndex,
				})
				parentWi = workItemCache.find((w) => w.id === parentWi?.parentId)
			}
			return {
				workItemName: wi.name,
				id: wi.id,
				hierarchy: hierarchy.reverse(),
			}
		})
	return workItemInfo
}
//#endregion

//#region WRITE
export const assignTaskFB = async (
	db: ForemanDB,
	workerId: number,
	taskId: number,
	taskType: string,
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'directReports',
			'reworks',
			'tasks',
			'workItems',
			'attendance',
			'timesheets',
			'timeRecords',
			'constraints',
			'workItemAssignments',
			'mutations',
			'reworkAssignments',
			'taskAssignments',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const task: WorkItemTask | ReworkTask | undefined =
				taskType === 'reworkTask'
					? await tx.table('reworks').get(taskId)
					: await tx.table('tasks').get(taskId)

			if (!task) {
				throw new Error('Task not found')
			}

			const worker = await tx.table('directReports').get(workerId)
			if (!worker) {
				throw new Error('Worker not found')
			}
			const workItem = await tx.table('workItems').get(task.workItemId!)
			if (!workItem) {
				throw new Error('Work item not found')
			}
			if (taskType === 'workItemTask') {
				const taskAssignments = await tx
					.table('taskAssignments')
					.where(['workerId', 'workItemTaskId'])
					.equals([workerId, taskId])
					.first()
				if (taskAssignments) {
					throw new Error('Task already assigned')
				}

				await tx.table('taskAssignments').add({ workerId, workItemTaskId: taskId })
			} else if (taskType === 'reworkTask') {
				const reworkAssignments = await tx
					.table('reworkAssignments')
					.where(['workerId', 'reworkId'])
					.equals([workerId, taskId])
					.first()
				if (reworkAssignments) {
					throw new Error('Rework already assigned')
				}
				await tx.table('reworkAssignments').add({ workerId, reworkId: taskId })
			}

			await tx.table('workItemAssignments').put({ workerId, workItemId: workItem.id })
			await tx.table('mutations').add({
				route: `/Workers/${workerId}/Tasks/${taskId}?type=${taskType}`,
				method: 'POST',
				body: null,
				title: `Assigned ${task.name} to ${worker.firstName} ${worker.lastName}`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
			return true
		},
	)
}
export const unassignTaskFB = async (
	db: ForemanDB,
	workerId: number,
	taskId: number,
	taskType: string,
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'directReports',
			'reworks',
			'tasks',
			'reworkAssignments',
			'taskAssignments',
			'workItems',
			'attendance',
			'timesheets',
			'timeRecords',
			'workItemAssignments',
			'constraints',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const worker = await tx.table('directReports').get(workerId)
			if (!worker) {
				throw new Error('Worker not found')
			}
			const task =
				taskType === 'reworkTask'
					? await tx.table('reworks').get(taskId)
					: await tx.table('tasks').get(taskId)
			if (!task) {
				throw new Error('Task not found')
			}
			// Delete the assignment and perform cleanup
			if (taskType === 'reworkTask') {
				await tx
					.table('reworkAssignments')
					.where(['workerId', 'reworkId'])
					.equals([workerId, taskId])
					.delete()
				await db.cleanupReworkAssignment(workerId, taskId, tx)
			} else {
				await tx
					.table('taskAssignments')
					.where(['workerId', 'workItemTaskId'])
					.equals([workerId, taskId])
					.delete()
				await db.cleanupTaskAssignment(workerId, taskId, tx)
			}

			await db.triggerWorkItemStatusUpdate(task.workItemId!, tx)
			await tx.table('mutations').add({
				route: `/Workers/${workerId}/Tasks/${taskId}?type=${taskType}`,
				method: 'DELETE',
				body: null,
				title: `Unassigned ${task.name} from ${worker.firstName} ${worker.lastName}`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
			return true
		},
	)
}
export const activateTaskFB = async (
	db: ForemanDB,
	workerId: number,
	taskId: number,
	taskType: string,
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'directReports',
			'reworks',
			'tasks',
			'workItems',
			'attendance',
			'timesheets',
			'timeRecords',
			'constraints',
			'workItemAssignments',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}

			const worker = await tx.table('directReports').get(workerId)
			if (!worker) {
				throw new Error('Worker not found')
			}
			const task: WorkItemTask | ReworkTask | undefined =
				taskType === 'reworkTask'
					? await tx.table('reworks').get(taskId)
					: await tx.table('tasks').get(taskId)
			if (!task) {
				throw new Error('Task not found')
			}
			const workItem = await tx.table('workItems').get(task.workItemId!)
			if (!workItem) {
				throw new Error('Work item not found')
			}
			if (workItem.status === 'Constrained') {
				throw new Error('Work item is constrained')
			}
			let currentTaskId = 0
			if (worker.activeTaskId) {
				await tx.table('tasks').update(worker.activeTaskId, { status: 'Inactive' })
				currentTaskId = worker.activeTaskId
			}
			if (worker.activeReworkId) {
				await tx.table('reworks').update(worker.activeReworkId, { status: 'Inactive' })
				currentTaskId = worker.activeReworkId
			}
			if (taskType === 'reworkTask') {
				await tx.table('directReports').update(workerId, {
					activeReworkId: taskId,
					activeTaskId: undefined,
				})
			} else {
				await tx.table('directReports').update(workerId, {
					activeTaskId: taskId,
					activeReworkId: undefined,
				})
			}

			taskType === 'reworkTask'
				? await tx.table('reworks').update(taskId, { status: 'Active' })
				: await tx.table('tasks').update(taskId, { status: 'Active' })
			await db.triggerWorkItemStatusUpdate(task.workItemId!, tx)

			if (currentTaskId) {
				const currentTask =	taskType === 'reworkTask'
						? await tx.table('reworks').get(currentTaskId)
						: await tx.table('tasks').get(currentTaskId)
				if (currentTask) {
					await db.triggerWorkItemStatusUpdate(currentTask.workItemId!, tx)
				}

			}

			await db.addTimeRecord(tx, workerId, false, '', task)

			await tx.table('mutations').add({
				route: `/Workers/${workerId}/ActiveTasks/${taskId}?type=${taskType}`,
				method: 'POST',
				body: null,
				title: `Activated ${task.name} for ${worker.firstName} ${worker.lastName}`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
			return true
		},
	)
}
export const deactivateTaskFB = async (
	db: ForemanDB,
	workerId: number,
	taskId: number,
	taskType: string,
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'directReports',
			'reworks',
			'tasks',
			'workItems',
			'attendance',
			'timesheets',
			'timeRecords',
			'constraints',
			'workItemAssignments',
		]),
		async () => {
			// Get the current transaction
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const worker = await tx.table('directReports').get(workerId)
			if (!worker) {
				throw new Error('Worker not found')
			}
			const task =
				taskType === 'reworkTask'
					? await tx.table('reworks').get(taskId)
					: await tx.table('tasks').get(taskId)
			if (!task) {
				throw new Error('Task not found')
			}
			await tx.table('tasks').update(taskId, { status: 'Inactive' })
			await tx.table('directReports').update(workerId, {
				activeTaskId: undefined,
				activeReworkId: undefined,
			})
			await db.triggerWorkItemStatusUpdate(task.workItemId!, tx)
			await db.addTimeRecord(tx, workerId, false)
			await tx.table('mutations').add({
				route: `/Workers/${workerId}/ActiveTasks/${taskId}?type=${taskType}`,
				method: 'DELETE',
				body: null,
				title: `Deactivated ${task.name} for ${worker.firstName} ${worker.lastName}`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
			return true
		},
	)
}

//#endregion
